import type { NativeContentsApi, NativeContentsBounds } from '@hibro/shared';
import { cn, useUiClientConfig } from '@hibro/ui-client';
import {
  type HTMLAttributes,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { observeFloatingLayerOverlap } from './floating-layer-observer';
import {
  normalizeSnapshotInterval,
  resolveFailedSnapshotFallback,
  shouldFreezeSnapshotCommit,
} from './native-contents-snapshot';
import {
  useNativeContentsSuppression,
  useNativeContentsSuppressionPreparation,
} from './native-contents-suppression';

export type NativeContentsLayerBounds = NativeContentsBounds;
export type NativeContentsLayerBridge = NativeContentsApi;

export interface NativeContentsLayerProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> {
  bridge?: NativeContentsLayerBridge;
  /** Opaque key registered by the native-content owner in the host window. */
  instanceKey?: string;
  visible?: boolean;
  /** Whether the instance has content worth snapshotting. */
  active?: boolean;
  /** Optional periodic refresh; zero disables it, positive values clamp to 1s. */
  snapshotIntervalMs?: number;
  onBridgeError?: (operation: string, error: unknown) => void;
}

/**
 * DOM adapter for native composited content.
 *
 * It reports measured bounds and owns one suppression pipeline for both host
 * modals and geometrically overlapping floating layers. Capture requests are
 * serialized, and snapshot commits freeze while suppression is requested.
 */
export function NativeContentsLayer({
  bridge,
  instanceKey,
  visible = true,
  active = true,
  snapshotIntervalMs = 0,
  onBridgeError,
  className,
  ...props
}: NativeContentsLayerProps): ReactNode {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const lastBoundsRef = useRef<NativeContentsLayerBounds | undefined>(
    undefined,
  );
  const mountedRef = useRef(true);
  const instanceGenerationRef = useRef(0);
  const transitionRef = useRef(0);
  const snapshotTimerRef = useRef(0);
  // Prevent an in-flight periodic capture from replacing the painted fallback
  // while the native layer is transitioning out.
  const suppressionRequestedRef = useRef(false);
  const snapshotUrlRef = useRef<string | undefined>(undefined);
  const paintedSnapshotUrlRef = useRef<string | undefined>(undefined);
  const retiredSnapshotUrlsRef = useRef(new Set<string>());
  const snapshotLoadWaitersRef = useRef(
    new Map<string, Set<(loaded: boolean) => void>>(),
  );
  const captureLoopRef = useRef<Promise<string | undefined> | undefined>(
    undefined,
  );
  const captureQueuedRef = useRef(false);
  const [snapshotUrl, setSnapshotUrl] = useState<string>();
  const [locallyOverlapping, setLocallyOverlapping] = useState(false);
  const globallySuppressed = useNativeContentsSuppression();
  const { portalContainer } = useUiClientConfig();
  const wantsSuppression = globallySuppressed || locallyOverlapping;
  const overlayHost = portalContainer;
  const invalidateTransition = useCallback((): void => {
    transitionRef.current++;
  }, []);

  const reportError = useCallback(
    (operation: string, error: unknown): void => {
      onBridgeError?.(operation, error);
    },
    [onBridgeError],
  );

  const settleSnapshotLoad = useCallback(
    (url: string, loaded: boolean): void => {
      if (loaded) {
        paintedSnapshotUrlRef.current = url;
      }
      const waiters = snapshotLoadWaitersRef.current.get(url);
      if (!waiters) {
        return;
      }
      snapshotLoadWaitersRef.current.delete(url);
      for (const settle of waiters) {
        settle(loaded);
      }
    },
    [],
  );

  const settleAllSnapshotLoads = useCallback((): void => {
    for (const waiters of snapshotLoadWaitersRef.current.values()) {
      for (const settle of waiters) {
        settle(false);
      }
    }
    snapshotLoadWaitersRef.current.clear();
  }, []);

  const releaseRetiredSnapshots = useCallback((): void => {
    for (const url of retiredSnapshotUrlsRef.current) {
      URL.revokeObjectURL(url);
    }
    retiredSnapshotUrlsRef.current.clear();
  }, []);

  const clearSnapshots = useCallback(
    (updateState = true): void => {
      settleAllSnapshotLoads();
      const currentUrl = snapshotUrlRef.current;
      snapshotUrlRef.current = undefined;
      paintedSnapshotUrlRef.current = undefined;
      if (updateState) {
        setSnapshotUrl(undefined);
      }
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
      releaseRetiredSnapshots();
    },
    [releaseRetiredSnapshots, settleAllSnapshotLoads],
  );

  const rollbackFailedSnapshot = useCallback(
    (failedUrl: string): void => {
      settleSnapshotLoad(failedUrl, false);
      if (snapshotUrlRef.current !== failedUrl) {
        return;
      }
      const paintedUrl = paintedSnapshotUrlRef.current;
      const fallbackUrl = resolveFailedSnapshotFallback(failedUrl, paintedUrl);
      if (fallbackUrl) {
        retiredSnapshotUrlsRef.current.delete(fallbackUrl);
      }
      releaseRetiredSnapshots();
      snapshotUrlRef.current = fallbackUrl;
      setSnapshotUrl(fallbackUrl);
      URL.revokeObjectURL(failedUrl);
    },
    [releaseRetiredSnapshots, settleSnapshotLoad],
  );

  const replaceSnapshot = useCallback(
    async (
      bytes: Uint8Array,
      generation: number,
    ): Promise<string | undefined> => {
      const copy = new Uint8Array(bytes);
      const nextUrl = URL.createObjectURL(
        new Blob([copy.buffer], { type: 'image/jpeg' }),
      );
      const image = new Image();
      image.src = nextUrl;
      try {
        await image.decode();
      } catch (error) {
        URL.revokeObjectURL(nextUrl);
        throw error;
      }
      if (!mountedRef.current || generation !== instanceGenerationRef.current) {
        URL.revokeObjectURL(nextUrl);
        return undefined;
      }
      const paintedUrl = paintedSnapshotUrlRef.current;
      if (
        shouldFreezeSnapshotCommit(
          suppressionRequestedRef.current,
          snapshotUrlRef.current,
          paintedUrl,
        )
      ) {
        URL.revokeObjectURL(nextUrl);
        return paintedUrl;
      }
      const previousUrl = snapshotUrlRef.current;
      snapshotUrlRef.current = nextUrl;
      setSnapshotUrl(nextUrl);
      if (previousUrl) {
        retiredSnapshotUrlsRef.current.add(previousUrl);
      }
      return nextUrl;
    },
    [],
  );

  const captureLatest = useCallback((): Promise<string | undefined> => {
    if (!bridge || !instanceKey || !active) {
      return Promise.resolve(snapshotUrlRef.current);
    }
    captureQueuedRef.current = true;
    if (captureLoopRef.current) {
      return captureLoopRef.current;
    }
    const loop = async (): Promise<string | undefined> => {
      let latestUrl = snapshotUrlRef.current;
      while (captureQueuedRef.current && mountedRef.current) {
        captureQueuedRef.current = false;
        const generation = instanceGenerationRef.current;
        try {
          const bytes = await bridge.captureSnapshot(instanceKey);
          if (
            bytes.byteLength > 0 &&
            mountedRef.current &&
            generation === instanceGenerationRef.current
          ) {
            latestUrl = await replaceSnapshot(bytes, generation);
          }
        } catch (error) {
          reportError('snapshot capture', error);
        }
      }
      return latestUrl;
    };
    const promise = loop().finally(() => {
      if (captureLoopRef.current === promise) {
        captureLoopRef.current = undefined;
      }
    });
    captureLoopRef.current = promise;
    return promise;
  }, [active, bridge, instanceKey, replaceSnapshot, reportError]);

  const waitForSnapshotLoad = useCallback((url: string): Promise<boolean> => {
    if (paintedSnapshotUrlRef.current === url) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      let timeout = 0;
      const finish = (loaded: boolean): void => {
        window.clearTimeout(timeout);
        const currentWaiters = snapshotLoadWaitersRef.current.get(url);
        currentWaiters?.delete(finish);
        if (currentWaiters?.size === 0) {
          snapshotLoadWaitersRef.current.delete(url);
        }
        resolve(loaded);
      };
      const waiters =
        snapshotLoadWaitersRef.current.get(url) ??
        new Set<(loaded: boolean) => void>();
      waiters.add(finish);
      snapshotLoadWaitersRef.current.set(url, waiters);
      timeout = window.setTimeout(() => finish(false), 2000);
    });
  }, []);

  const prepareNativeSuppression = useCallback(async (): Promise<void> => {
    if (!bridge || !instanceKey) {
      return;
    }
    const transition = ++transitionRef.current;
    const generation = instanceGenerationRef.current;
    suppressionRequestedRef.current = true;
    const current = (): boolean =>
      mountedRef.current &&
      generation === instanceGenerationRef.current &&
      transition === transitionRef.current;
    const currentUrl = snapshotUrlRef.current;
    const hasPaintedSnapshot =
      currentUrl !== undefined && paintedSnapshotUrlRef.current === currentUrl;
    if (!hasPaintedSnapshot) {
      const preparedUrl = await captureLatest();
      if (preparedUrl && current()) {
        await waitForSnapshotLoad(preparedUrl);
      }
    }
    if (!current()) {
      return;
    }
    try {
      await bridge.setSuppressed(instanceKey, true);
      if (!current()) {
        await bridge.setSuppressed(instanceKey, false);
      }
    } catch (error) {
      reportError('layer suppression update', error);
    }
  }, [bridge, captureLatest, instanceKey, reportError, waitForSnapshotLoad]);
  useNativeContentsSuppressionPreparation(prepareNativeSuppression);

  // Reset the instance before the suppression layout effect below can start.
  // A passive reset used to invalidate that first preparation when this layer
  // mounted underneath an already-open modal, leaving the native view visible
  // above the modal until suppression changed again.
  useLayoutEffect(() => {
    mountedRef.current = true;
    instanceGenerationRef.current++;
    transitionRef.current++;
    suppressionRequestedRef.current = false;
    lastBoundsRef.current = undefined;
    captureQueuedRef.current = false;
    setLocallyOverlapping(false);
    clearSnapshots();
  }, [clearSnapshots, instanceKey]);

  useEffect(
    () => () => {
      mountedRef.current = false;
      instanceGenerationRef.current++;
      transitionRef.current++;
      suppressionRequestedRef.current = false;
      captureQueuedRef.current = false;
      clearSnapshots(false);
    },
    [clearSnapshots],
  );

  useEffect(() => {
    if (!bridge || !instanceKey) {
      return;
    }
    void bridge
      .setVisible(instanceKey, visible)
      .catch((error: unknown) => reportError('visibility update', error));
    return () => {
      void bridge
        .setVisible(instanceKey, false)
        .catch((error: unknown) => reportError('visibility update', error));
    };
  }, [bridge, instanceKey, reportError, visible]);

  useEffect(() => {
    if (!bridge || !instanceKey) {
      return;
    }
    return () => {
      void bridge
        .setSuppressed(instanceKey, false)
        .catch((error: unknown) =>
          reportError('layer suppression update', error),
        );
    };
  }, [bridge, instanceKey, reportError]);

  const reportBounds = useCallback((): void => {
    const element = targetRef.current;
    if (!bridge || !instanceKey || !element) {
      return;
    }
    const rect = element.getBoundingClientRect();
    const viewport = element.ownerDocument.defaultView;
    const left = Math.max(
      0,
      Math.min(rect.left, viewport?.innerWidth ?? rect.left),
    );
    const top = Math.max(
      0,
      Math.min(rect.top, viewport?.innerHeight ?? rect.top),
    );
    const right = Math.max(
      left,
      Math.min(rect.right, viewport?.innerWidth ?? rect.right),
    );
    const bottom = Math.max(
      top,
      Math.min(rect.bottom, viewport?.innerHeight ?? rect.bottom),
    );
    const bounds: NativeContentsLayerBounds = {
      x: left,
      y: top,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top),
    };
    const previous = lastBoundsRef.current;
    if (
      previous?.x === bounds.x &&
      previous.y === bounds.y &&
      previous.width === bounds.width &&
      previous.height === bounds.height
    ) {
      return;
    }
    lastBoundsRef.current = bounds;
    void bridge
      .setBounds(instanceKey, bounds)
      .catch((error: unknown) => reportError('bounds update', error));
  }, [bridge, instanceKey, reportError]);

  useEffect(() => {
    const element = targetRef.current;
    if (!bridge || !instanceKey || !element) {
      return;
    }
    let disposed = false;
    let frame = 0;
    const schedule = (): void => {
      if (disposed || frame !== 0) {
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (!disposed) {
          reportBounds();
        }
      });
    };
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(schedule);
    observer?.observe(element);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    const unsubscribeZoom = bridge.onRemeasure?.(() => {
      lastBoundsRef.current = undefined;
      schedule();
    });
    schedule();
    return () => {
      disposed = true;
      if (frame !== 0) {
        cancelAnimationFrame(frame);
      }
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      unsubscribeZoom?.();
    };
  }, [bridge, instanceKey, reportBounds]);

  useEffect(() => {
    window.clearTimeout(snapshotTimerRef.current);
    if (!bridge || !instanceKey || !visible || !active || wantsSuppression) {
      return;
    }
    let disposed = false;
    const schedule = async (): Promise<void> => {
      await captureLatest();
      if (!disposed && snapshotIntervalMs > 0) {
        snapshotTimerRef.current = window.setTimeout(
          () => void schedule(),
          normalizeSnapshotInterval(snapshotIntervalMs),
        );
      }
    };
    void schedule();
    return () => {
      disposed = true;
      window.clearTimeout(snapshotTimerRef.current);
    };
  }, [
    active,
    bridge,
    captureLatest,
    instanceKey,
    snapshotIntervalMs,
    visible,
    wantsSuppression,
  ]);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) {
      return;
    }
    return observeFloatingLayerOverlap(target, setLocallyOverlapping, {
      portalHost: overlayHost,
    });
  }, [overlayHost]);

  useLayoutEffect(() => {
    if (!bridge || !instanceKey) {
      return;
    }
    if (wantsSuppression) {
      void prepareNativeSuppression();
    } else {
      suppressionRequestedRef.current = false;
      invalidateTransition();
      void bridge
        .setSuppressed(instanceKey, false)
        .catch((error: unknown) =>
          reportError('layer suppression update', error),
        );
    }
    return () => {
      invalidateTransition();
    };
  }, [
    bridge,
    instanceKey,
    invalidateTransition,
    prepareNativeSuppression,
    reportError,
    wantsSuppression,
  ]);

  return (
    <div
      ref={targetRef}
      className={cn(
        'relative min-h-0 overflow-hidden bg-background',
        className,
      )}
      aria-hidden="true"
      {...props}
    >
      {snapshotUrl ? (
        <img
          src={snapshotUrl}
          alt=""
          className={cn(
            'pointer-events-none absolute inset-0 h-full w-full object-fill',
            active && visible ? 'visible' : 'invisible',
          )}
          draggable={false}
          onLoad={(event) => {
            const url =
              event.currentTarget.currentSrc || event.currentTarget.src;
            settleSnapshotLoad(url, true);
            releaseRetiredSnapshots();
          }}
          onError={(event) => {
            const url =
              event.currentTarget.currentSrc || event.currentTarget.src;
            rollbackFailedSnapshot(url);
            reportError(
              'snapshot render',
              new Error('Native content snapshot failed to render'),
            );
          }}
        />
      ) : null}
    </div>
  );
}
